import { useEffect, useState } from "react";
import { listFlussMetadata } from "../node-properties/useFlussNodePanelMetadata";
import { metadataToScopeOptions } from "../node-properties/fluss-node-panel-utils";
import { ScopeOption } from "../fluss-builder-types";

// Columns are loaded on demand, one table at a time, from the metadata endpoint the Studio
// already uses (sys_columns by sys_table_id, which already includes inherited columns). One
// promise per table name is kept for the lifetime of the page.
const tablesPromise: { current: Promise<any[]> | null } = { current: null };
const columnsPromises = new Map<string, Promise<ScopeOption[]>>();

export const resetTableColumnsCache = () => {
    tablesPromise.current = null;
    columnsPromises.clear();
};

export const loadTableColumns = (tableName: string): Promise<ScopeOption[]> => {
    const cached = columnsPromises.get(tableName);
    if (cached) return cached;
    const promise = (async () => {
        if (!tablesPromise.current) tablesPromise.current = Promise.resolve(listFlussMetadata("sys_tables"));
        const tables = (await tablesPromise.current) || [];
        const table = tables.find((candidate: any) => candidate.name === tableName);
        if (!table?.id) return [];
        return metadataToScopeOptions(await listFlussMetadata("sys_columns", { sys_table_id: table.id }));
    })().catch(() => {
        columnsPromises.delete(tableName);
        tablesPromise.current = null;
        return [] as ScopeOption[];
    });
    columnsPromises.set(tableName, promise);
    return promise;
};

export const useTableColumns = (tableName?: string | null): { columns: ScopeOption[]; loading: boolean } => {
    const [state, setState] = useState<{ table: string; columns: ScopeOption[] } | null>(null);
    useEffect(() => {
        if (!tableName) return undefined;
        let active = true;
        loadTableColumns(tableName).then((columns) => {
            if (active) setState({ table: tableName, columns });
        });
        return () => { active = false; };
    }, [tableName]);
    if (!tableName) return { columns: [], loading: false };
    return state?.table === tableName
        ? { columns: state.columns, loading: false }
        : { columns: [], loading: true };
};

export default useTableColumns;
